import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { isSameDate } from 'app/helpers/isSameDate';
import { ExposedCreateAzureEnvironmentReservationReq } from 'app/interfaces/requests/Exposed/exposedCreateAzureEnvironmentReservationReq';

@Component({
    selector: 'app-azure-environment-reservations-add',
    templateUrl: './azure-environment-reservations-add.component.html'
})
export class AzureEnvironmentReservationsAddComponent implements OnInit {
    @Input() selectedEnvironment: string = '';
    @Output() save = new EventEmitter<ExposedCreateAzureEnvironmentReservationReq>();
    @Output() cancel = new EventEmitter<void>();

    form!: UntypedFormGroup;
    today: Date;

    startTimeOptions: string[] = [];
    endTimeOptions: string[] = [];

    constructor(private fb: UntypedFormBuilder) {
        // Initialize today as the current date with time set to 00:00
        this.today = new Date();
        this.today.setHours(0, 0, 0, 0);

        this.initTimeOptions();
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            reservationStartDate: [null, Validators.required],
            reservationStartTime: ['', Validators.required],
            reservationStartNow: [false],
            reservationEndDate: [null, Validators.required],
            reservationEndTime: ['', Validators.required],
            reservationTicketId: ['', [Validators.required, Validators.pattern(/^[0-9]+$/)]],
            reason: ['', Validators.required],
        });
    }

    // Time options for the dropdown, every 30 minutes from 00:00 to 23:30
    initTimeOptions(): void {
        this.startTimeOptions = this.generateTimeOptions();
        this.endTimeOptions = this.generateTimeOptions();
    }

    generateTimeOptions(): string[] {
        return Array.from({ length: 48 }, (_, i) => {
            const h = Math.floor(i / 2)
                .toString()
                .padStart(2, '0');
            const m = i % 2 === 0 ? '00' : '30';
            return `${h}:${m}`;
        });
    }

    get minEndDate(): Date {
        const start = this.form?.value.reservationStartDate;
        if (start) {
            const d = new Date(start);
            d.setHours(0, 0, 0, 0);
            return d;
        }
        return this.today;
    }

    isStartTimeDisabled(time: string): boolean {
        const startDate = this.form.value.reservationStartDate;
        if (!startDate) {
            return false;
        }

        const startDateOnly = new Date(startDate);
        if (!isSameDate(startDateOnly, this.today)) {
            return false;
        }

        const [h, m] = time.split(':').map(Number);
        const slot = new Date(startDate);
        slot.setHours(h, m, 0, 0);
        return slot <= new Date();
    }

    isEndTimeDisabled(time: string): boolean {
        const endDate = this.form.value.reservationEndDate;
        if (!endDate) {
            return false;
        }

        // Time is disabled if it is before the current time
        const [h, m] = time.split(':').map(Number);
        const endSlot = new Date(endDate);
        endSlot.setHours(h, m, 0, 0);
        if (endSlot <= new Date()) {
            return true;
        }

        const startDate = this.form.value.reservationStartDate;
        const startTime = this.form.value.reservationStartTime;
        if (!startDate || !startTime) {
            return false;
        }

        if (!isSameDate(startDate, endDate)) {
            return false;
        }

        // Time is disabled if it is before the start time on the same day
        const [sh, sm] = startTime.split(':').map(Number);
        const startSlot = new Date(startDate);
        startSlot.setHours(sh, sm, 0, 0);
        return endSlot <= startSlot;
    }

    blockNonDigit(event: KeyboardEvent): void {
        if (!/[0-9]/.test(event.key)) {
            event.preventDefault();
        }
    }

    onStartDateChange(): void {
        const startDate = this.form.value.reservationStartDate;
        const endDate = this.form.value.reservationEndDate;
        if (startDate && endDate) {
            const s = new Date(startDate);
            s.setHours(0, 0, 0, 0);
            const e = new Date(endDate);
            e.setHours(0, 0, 0, 0);
            if (e < s) this.form.patchValue({ reservationEndDate: null, reservationEndTime: '' });
        }
        const startTime = this.form.value.reservationStartTime;
        if (startTime && this.isStartTimeDisabled(startTime)) {
            this.form.patchValue({ reservationStartTime: '' });
        }
    }

    onReserveFromNowChange(): void {
        const isChecked = this.form.value.reservationStartNow;
        if (isChecked) {
            const startTimeDate = new Date();
            const startTimeOnly = `${startTimeDate.getHours().toString().padStart(2, '0')}:${startTimeDate.getMinutes().toString().padStart(2, '0')}`;
            this.startTimeOptions = [startTimeOnly];
            this.form.patchValue({
                reservationStartDate: startTimeDate,
                reservationStartTime: startTimeOnly,
            });
            this.form.controls.reservationStartDate.disable();
            this.form.controls.reservationStartTime.disable();
        } else {
            this.form.patchValue({
                reservationStartDate: null,
                reservationStartTime: '',
            });
            this.initTimeOptions();
            this.form.controls.reservationStartDate.enable();
            this.form.controls.reservationStartTime.enable();
        }
    }

    onStartTimeChange(): void {
        const endTime = this.form.value.reservationEndTime;
        if (endTime && this.isEndTimeDisabled(endTime)) {
            this.form.patchValue({ reservationEndTime: '' });
        }
    }

    onEndDateChange(): void {
        const endTime = this.form.value.reservationEndTime;
        if (endTime && this.isEndTimeDisabled(endTime)) {
            this.form.patchValue({ reservationEndTime: '' });
        }
    }

    submit(): void {
        if (this.form.invalid) {
            return;
        }

        const rawValue = this.form.getRawValue();

        const startDate = new Date(rawValue.reservationStartDate);
        const [startHours, startMinutes] = (rawValue.reservationStartTime as string).split(':').map(Number);
        startDate.setHours(startHours, startMinutes, 0, 0);

        const endDate = new Date(rawValue.reservationEndDate);
        const [endHours, endMinutes] = (rawValue.reservationEndTime as string).split(':').map(Number);
        endDate.setHours(endHours, endMinutes, 0, 0);

        this.save.emit({
            environmentName: this.selectedEnvironment,
            reservationStartTime: startDate,
            reservationEndTime: endDate,
            reservationTicketId: rawValue.reservationTicketId,
            reason: rawValue.reason,
        });
    }
}
